/**
 * Announcements Settings Component
 *
 * Provides configuration interface and announcements management.
 * Allows studio owners/managers to create, edit, and delete announcements.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Palette as PaletteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  useGetAnnouncementConfigQuery,
  useUpdateAnnouncementConfigMutation,
} from '../../api/announcementsApi';
import AnnouncementsList from './AnnouncementsList';

interface AnnouncementsSettingsProps {
  studioId: string;
}

const AnnouncementsSettings: React.FC<AnnouncementsSettingsProps> = ({
  studioId,
}) => {
  const navigate = useNavigate();
  const [copySuccess, setCopySuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch current configuration
  const {
    data: config,
    isLoading,
    error,
  } = useGetAnnouncementConfigQuery(studioId, {
    skip: !studioId,
  });

  // Update mutation
  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateAnnouncementConfigMutation();

  // Form state
  const [greetingMessage, setGreetingMessage] = useState(
    'Important Announcements'
  );

  // Update form state when config loads
  React.useEffect(() => {
    if (config) {
      setGreetingMessage(config.greeting_message);
    }
  }, [config]);

  const handleSave = async () => {
    if (!studioId) return;

    try {
      await updateConfig({
        studioId,
        data: {
          greeting_message: greetingMessage,
        },
      }).unwrap();
      setSaveSuccess(true);
    } catch (err) {
      console.error('Failed to update announcement config:', err);
    }
  };

  const handleCopyEmbedCode = () => {
    if (config?.embed_code) {
      navigator.clipboard.writeText(config.embed_code);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleViewPublicPage = () => {
    if (config?.embed_code) {
      const match = config.embed_code.match(/src="([^"]+)"/);
      if (match && match[1]) {
        window.open(match[1], '_blank');
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSaveSuccess(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load announcements configuration. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Announcements List */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <AnnouncementsList studioId={studioId} />
      </Paper>

      {/* Configuration Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Widget Settings
        </Typography>

        <TextField
          fullWidth
          label="Greeting Message (Optional)"
          value={greetingMessage}
          onChange={e => setGreetingMessage(e.target.value)}
          helperText="Leave blank to hide the greeting message on the public page"
          sx={{ mb: 3 }}
        />

        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<PaletteIcon />}
            onClick={() => navigate('/theme')}
            fullWidth
          >
            Customize Theme Colors
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={1}
          >
            The announcements widget uses your studio&apos;s theme colors
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isUpdating}
          fullWidth
        >
          {isUpdating ? 'Saving...' : 'Save Settings'}
        </Button>
      </Paper>

      {/* Embed Code */}
      {config?.embed_code && (
        <Card>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Embed Code</Typography>
              <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton onClick={handleCopyEmbedCode} color="primary">
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Copy this code to embed the announcements widget on your website:
            </Typography>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflowX: 'auto',
                mb: 2,
              }}
            >
              {config.embed_code}
            </Paper>

            {config.embed_code && (
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handleViewPublicPage}
                fullWidth
              >
                View Public Announcements Page
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          Announcements settings saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnnouncementsSettings;
